import React, { useCallback, useEffect, useState } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  message,
  Space,
  Tag,
  Divider,
  DatePicker,
  Select,
  Checkbox,
} from 'antd';
import {
  getAdminUsers,
  adminUpdateUser,
  adminBindShop,
  adminUnbindShop,
  adminDeleteUser,
  adminActiveShop,
  adminResetUserPassword,
} from '../services/api';
import axios from 'axios';
import moment from 'moment';

const AdminUserManagement = () => {
  const [couponCodes, setCouponCodes] = useState('');
  const [couponResults, setCouponResults] = useState([]);
  const [couponModalVisible, setCouponModalVisible] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [shopModalVisible, setShopModalVisible] = useState(false);
  const [currentShopUser, setCurrentShopUser] = useState(null);
  const [shopId, setShopId] = useState('');
  const [usernameSearch, setUsernameSearch] = useState('');
  const [emailSearch, setEmailSearch] = useState('');
  const [notExpiredOnly, setNotExpiredOnly] = useState(false);
  const [form] = Form.useForm();

  const handleCheckCoupons = async () => {
    try {
      const codes = couponCodes.split('\n').filter((code) => code.trim());
      const results = await Promise.all(
        codes.map(async (code) => {
          try {
            const res = await axios.get(
              `https://hn.api.yesapi.net/?s=App.Market_Kami.Take&uuid=ddddd&token=ffff&return_data=0&kami=${code.trim()}&app_key=C368C518EE6FAE05FBF9F083995B0D34&sign=F1B14B2A8F49F6E9F39ADBC82F605B10`
            );
            const kamiTakenTime = res?.data?.data?.kami_detail?.kami_taken_time;
            const kamiExpireTime = res?.data?.data?.kami_detail?.kami_expire_time;
            const leftDays = kamiExpireTime
              ? Math.floor((new Date(kamiExpireTime).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
              : null;

            return {
              code,
              valid: true,
              kami_taken_time: kamiTakenTime,
              kami_expire_time: kamiExpireTime,
              leftDays,
            };
          } catch {
            return { code, valid: false };
          }
        })
      );

      setCouponResults(results);
      setCouponModalVisible(true);
    } catch (error) {
      message.error(`查询激活码失败: ${error.message}`);
    }
  };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (usernameSearch) params.username = usernameSearch;
      if (emailSearch) params.email = emailSearch;
      if (notExpiredOnly) params.notExpired = true;
      const res = await getAdminUsers(params);
      setUsers(res?.users || []);
    } catch (error) {
      message.error(error.message);
    } finally {
      setLoading(false);
    }
  }, [usernameSearch, emailSearch, notExpiredOnly]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleEdit = (user) => {
    setEditingUser(user);
    form.setFieldsValue({
      ...user,
      membership: {
        ...user.membership,
        maxShops: parseInt(user.membership?.maxShops, 10) || 0,
        expiry: user.membership?.expiry ? moment(user.membership.expiry) : null,
        feature: user.membership?.feature || 'all',
      },
    });
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const updateData = {
        membership: {
          maxShops: values.membership.maxShops,
          expiry: values.membership.expiry,
          feature: values.membership.feature || 'all',
        },
      };
      await adminUpdateUser(editingUser._id, updateData);
      message.success('会员信息更新成功');
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      message.error(error.message);
    }
  };

  const handleManageShops = (user) => {
    setCurrentShopUser(user);
    setShopModalVisible(true);
  };

  const handleBindShop = async () => {
    try {
      await adminBindShop(shopId, currentShopUser._id);
      message.success('店铺绑定成功');
      fetchUsers();
      setShopId('');
    } catch (error) {
      message.error(error.message);
    }
  };

  const handleUnbindShop = async (targetShopId, userId) => {
    try {
      await adminUnbindShop(targetShopId, userId);
      message.success('店铺解绑成功');
      fetchUsers();
    } catch (error) {
      message.error(error.message);
    }
  };

  const handleActiveShop = async (targetShopId, userId, isActive) => {
    try {
      await adminActiveShop(targetShopId, userId, isActive);
      message.success('店铺状态更新成功');
      fetchUsers();
    } catch (error) {
      message.error(error.message);
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await adminDeleteUser(userId);
      message.success('用户删除成功');
      fetchUsers();
    } catch (error) {
      message.error(error.message);
    }
  };

  const handleSendPasswordReset = async (userId) => {
    try {
      await adminResetUserPassword(userId);
      message.success('临时密码已生成并发送到用户邮箱');
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    }
  };

  const columns = [
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: '创建日期',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (text, record) => {
        let date;
        if (typeof text === 'string') {
          date = new Date(text);
        } else if (record._id) {
          date = new Date(parseInt(record._id.toString().substring(0, 8), 16) * 1000);
        }
        return Number.isNaN(date?.getTime()) ? '-' : date.toLocaleString();
      },
    },
    {
      title: '会员等级',
      dataIndex: ['membership', 'level'],
      key: 'level',
    },
    {
      title: '过期时间',
      dataIndex: ['membership', 'expiry'],
      key: 'expiry',
      render: (expiry) => (expiry ? new Date(expiry).toLocaleString() : '-'),
    },
    {
      title: '店铺数量',
      dataIndex: 'shops',
      key: 'shops',
      render: (shops, record) => `${shops?.length || 0}/${record.membership?.maxShops || 0}`,
    },
    {
      title: '功能',
      dataIndex: ['membership', 'feature'],
      key: 'feature',
      render: (feature) => feature || 'all',
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button onClick={() => handleEdit(record)}>编辑</Button>
          <Button onClick={() => handleManageShops(record)}>管理店铺</Button>
          <Button onClick={() => handleSendPasswordReset(record._id)}>发送临时密码</Button>
          <Button danger onClick={() => handleDeleteUser(record._id)}>
            删除
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ marginBottom: 16 }}>
        <Input
          placeholder="搜索用户名"
          value={usernameSearch}
          onChange={(e) => setUsernameSearch(e.target.value)}
          style={{ width: 200 }}
        />
        <Input
          placeholder="搜索邮箱"
          value={emailSearch}
          onChange={(e) => setEmailSearch(e.target.value)}
          style={{ width: 200 }}
        />
        <Checkbox
          checked={notExpiredOnly}
          onChange={(e) => setNotExpiredOnly(e.target.checked)}
        >
          仅显示未过期用户
        </Checkbox>
      </Space>

      <Table columns={columns} dataSource={users} loading={loading} rowKey="_id" />

      <Modal title="编辑用户信息" open={!!editingUser} onCancel={() => setEditingUser(null)} onOk={handleSave}>
        <Form form={form} layout="vertical">
          <Form.Item label="旧激活码">
            <Space>
              <Input.TextArea
                rows={4}
                placeholder="每行输入一个激活码"
                value={couponCodes}
                onChange={(e) => setCouponCodes(e.target.value)}
              />
              <Button type="primary" onClick={handleCheckCoupons} disabled={!couponCodes.trim()}>
                查询
              </Button>
            </Space>
          </Form.Item>
          <Form.Item name="username" label="用户名" rules={[{ required: true }]}> 
            <Input />
          </Form.Item>
          <Form.Item name="email" label="邮箱" rules={[{ required: true }]}> 
            <Input />
          </Form.Item>
          <Form.Item name={['membership', 'expiry']} label="会员有效期" rules={[{ required: true }]}>
            <DatePicker showTime={{ format: 'HH:mm:ss' }} format="YYYY-MM-DD HH:mm:ss" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name={['membership', 'maxShops']} label="最大店铺数量" rules={[{ required: true }]}>
            <Input type="number" min={0} />
          </Form.Item>
          <Form.Item name={['membership', 'feature']} label="功能" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'all', label: 'all' },
                { value: 'getFaildSku', label: 'getFaildSku' },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`管理店铺 - ${currentShopUser?.username || ''}`}
        open={shopModalVisible}
        onCancel={() => setShopModalVisible(false)}
        footer={null}
      >
        <div>
          <h4>当前店铺列表</h4>
          {currentShopUser?.shops?.length > 0 ? (
            <ul>
              {currentShopUser.shops.map((shop, index) => (
                <li key={`${shop.shopId}-${index}`} style={{ marginBottom: 8 }}>
                  <Space>
                    <span>{shop.shopId}</span>
                    <span>(绑定时间: {shop.bindDate ? new Date(shop.bindDate).toLocaleString() : '-'})</span>
                    <Tag color={shop.isActive ? 'green' : 'red'}>{shop.isActive ? '已启用' : '已停用'}</Tag>
                    <Button size="small" onClick={() => handleActiveShop(shop.shopId, currentShopUser._id, !shop.isActive)}>
                      {shop.isActive ? '停用' : '启用'}
                    </Button>
                    <Button size="small" danger onClick={() => handleUnbindShop(shop.shopId, currentShopUser._id)}>
                      解绑
                    </Button>
                  </Space>
                </li>
              ))}
            </ul>
          ) : (
            <p>暂无绑定店铺</p>
          )}

          <Divider />

          <Space>
            <Input placeholder="输入店铺ID" value={shopId} onChange={(e) => setShopId(e.target.value)} />
            <Button type="primary" onClick={handleBindShop} disabled={!shopId}>
              绑定新店铺
            </Button>
          </Space>
        </div>
      </Modal>

      <Modal title="激活码查询结果" open={couponModalVisible} onCancel={() => setCouponModalVisible(false)} footer={null} width={800}>
        <Table
          columns={[
            { title: '激活码', dataIndex: 'code' },
            {
              title: '状态',
              dataIndex: 'valid',
              render: (valid) => <Tag color={valid ? 'green' : 'red'}>{valid ? '有效' : '无效'}</Tag>,
            },
            { title: '使用时间', dataIndex: 'kami_taken_time' },
            { title: '到期时间', dataIndex: 'kami_expire_time' },
            { title: '剩余天数', dataIndex: 'leftDays' },
          ]}
          dataSource={couponResults}
          rowKey="code"
        />
      </Modal>
    </div>
  );
};

export default AdminUserManagement;
